import { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    Grid,
    Breadcrumbs,
    Typography,
    Button,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    Table,
    Paper,
    TableBody,
    IconButton,
    Switch,
    Stack,
} from '@mui/material';
import { Add, Visibility } from '@mui/icons-material';
import HomeIcon from '@mui/icons-material/Home';
import LinkNavegacao from '../../../common/Link';
import PageTitle from '../../../common/PageTitle';
import { EditIcon, PuzzleIcon, TrashIcon } from '../../../common/Icons';
import {atualizaSetor, deletaSetor, listaAreas, listaSetores} from './configs/functions';
import { checkPermissionsAndRedirect, hasPermission } from '../../../../configs/functions';
import { LoadingTableRow } from '../../../common/LoadingTableRow';
import { EmptyTableRow } from '../../../common/EmptyTableRow';
import { useCommonItems } from '../../../../contexts/CommonItensProvider';
import { useAppContext } from '../../../../contexts/AppContext';
import ConfirmDialog from '../../../common/ConfirmDialog';
import { CustomTablePagination } from '../../../common/CustomTablePagination';
import { listaEmpresas } from '../companies/configs/functions';
import { SelectEmpresaFilter } from '../../../common/SelectEmpresaFilter';
import { SectorForm } from './SectorForm';
import { useModuleContext } from '../../../../contexts/ModuleProvider';
import { CustomToolbarExportTable } from '../../../common/CustomToolbarExportTable';
import {useSearchParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {FiltersButton} from "../../../common/FiltersButton";
import {ItemsTableFilter, SectorsTableFilter} from "./components/ItemsTableFilter";

const SectorRow = ({ data, actionsAllowed, handleExibirAlerta, handleOpenEditForm, handleUpdateStatus, updateData }) => {
    const [active, setActive] = useState(data.status);
    const [openDialog, setOpenDialog] = useState(false);

    async function handleToggleStatus() {
        if (!hasPermission(["admin", "admin_setores", "update_setores"])) {
            handleExibirAlerta('Erro', 'Você não tem permissão para executar esta ação.', 'error')
            return false;
        }

        try {
            const res = await atualizaSetor(data.id, { status: !active })
            if (res) {
                const message = active ? 'Setor inativado com sucesso' : 'Setor ativado com sucesso'
                handleExibirAlerta('Sucesso', message, 'success')
                setActive(!active)
                handleUpdateStatus(data.id, !data.status)
            }
        } catch (error) {
            handleExibirAlerta('Erro', 'Erro ao alterar status', 'error')
            console.log(error)
        }
    }

    const deleteSector = () => {
        const id = data.id;
        deletaSetor(id)
            .then((response) => {
                const message = response.data.message ?? 'Setor excluído com sucesso'
                handleExibirAlerta('Sucesso', message, 'success');
                handleUpdateStatus(id, false);
                setActive(false);
                updateData();
            }).catch((error) => {
                const message = error.response.data.message ?? 'Erro ao executar operação'
                handleExibirAlerta('Erro', message, 'error');
            })

        setOpenDialog(false)
    }

    return (
        <>
            <TableRow key={data.id}>
                <TableCell>{data.titulo}</TableCell>
                <TableCell>{data.codExterno}</TableCell>
                <TableCell>{data.descricao}</TableCell>

                <TableCell>
                    <Switch
                        checked={active}
                        onChange={handleToggleStatus}
                        disabled={!hasPermission(["admin", "admin_setores", "update_setores"]) || !actionsAllowed}
                    />
                </TableCell>

                <TableCell align='right' sx={{ minWidth: 110 }}>
                    {hasPermission(["admin", "admin_setores", "update_setores"]) &&
                        <IconButton
                            aria-label="Editar"
                            onClick={() => handleOpenEditForm(data)}
                        >
                            {actionsAllowed ? <EditIcon /> : <Visibility color='primary' />}
                        </IconButton>
                    }

                    {hasPermission(["admin", "admin_setores", "delete_setores"]) && actionsAllowed &&
                        <IconButton
                            aria-label="Excluir"
                            onClick={() => setOpenDialog(true)}
                        >
                            <TrashIcon />
                        </IconButton>
                    }
                </TableCell>
            </TableRow>

            <ConfirmDialog
                description={'Tem certeza que deseja excluir este Setor?'}
                title={`Excluir Setor ${data.nome}`}
                goAction={deleteSector}
                handleClose={setOpenDialog}
                state={openDialog}
            />
        </>
    )
}

function Sectors() {
    const { exibirAlerta } = useCommonItems();
    const { getEmpresaIdSession, setEmpresaIdSession } = useAppContext();
    const { verifyIntegrationMode, getEmpresasFilteredByModule } = useModuleContext();

    const [moduleActionsAllowed, setModuleActionsAllowed] = useState(null);

    // estados para controle de paginacao
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [numTotalItems, setNumTotalItems] = useState(0);

    const [setores, setSetores] = useState([]);
    const [empresas, setEmpresas] = useState([]);
    const [selectedEmpresaId, setSelectedEmpresaId] = useState(null);
    const [openForm, setOpenForm] = useState(false);
    const [selectedSector, setSelectedSector] = useState(null);

    const [isOpenFilters, setIsOpenFilters] = useState(false);
    const [activeFilters, setActiveFilters] = useState(0);

    const [searchParams, setSearchParams] = useSearchParams();
    const searchParamPage = searchParams.get('page');
    const searchParamPerPage = searchParams.get('perPage');

    const columns = useMemo(() => [
        { field: 'titulo', headerName: 'Setor' },
        { field: 'codExterno', headerName: 'Cód. Externo' },
        { field: 'descricao', headerName: 'Descrição' },
        { field: 'status', headerName: 'Status', valueGetter: (params) => params.row.status === 1 ? 'Ativo' : 'Inativo' },
    ], []);

    const { data: empresasData, error: listaEmpresasError } = useQuery({
        queryKey: ['listaEmpresas'],
        queryFn: async () => {
            const response = await listaEmpresas(null, null, null, true);
            const empresasData = response.data.data;

            const selectedEmpresaId = getEmpresaIdSession() || empresasData[0]?.id;

            setEmpresaIdSession(selectedEmpresaId);

            setSearchParams((state) => {
                state.set('id_empresa', String(selectedEmpresaId));
                state.set('page', String(page + 1));
                state.set('perPage', String(rowsPerPage));

                return state;
            });
            return response;
        },
        refetchOnWindowFocus: false,
    });

    const queryFilters = {
        id_empresas: Array.isArray(searchParams.get('id_empresa')) ? searchParams.get('id_empresa') : [searchParams.get('id_empresa')],
        titulo: searchParams.get('nome'),
        cod_externo: searchParams.get('cod_externo'),
        limit: rowsPerPage,
        offset: page * rowsPerPage,
    };

    const queryKeyListSectors = ['listOfSectors', queryFilters];
    const { data: sectorsData, isLoading, error: sectorsError, refetch } = useQuery({
        queryKey: queryKeyListSectors,
        queryFn: () => listaSetores(queryFilters),
        enabled: !!searchParamPage && !! searchParamPerPage
    })

    function handleEditSector(item) {
        setSelectedSector(item);
        setOpenForm(true);
    }

    const updateSectorStatus = (sectorId, status) => setSetores(
        setores.map((item) => {
            if (item.id === sectorId) {
                item.status = status;
            }
            return item;
        })
    );

    useEffect(() => {
        if (listaEmpresasError) {
            exibirAlerta('Erro', 'Erro ao carregar empresas', 'error');
        }

        if (sectorsError) {
            exibirAlerta('Erro', 'Erro ao carregar setores', 'error');
        }
    }, [exibirAlerta, listaEmpresasError, sectorsError])

    useEffect(() => {
        if (sectorsData) {
            setSetores(sectorsData.data.data);
            setNumTotalItems(sectorsData.data.numero_total);
            setSelectedEmpresaId(searchParams.get('id_empresa'));
            setEmpresas(empresasData?.data?.data)
        }
    }, [sectorsData]);

    useEffect(() => {
        if (selectedEmpresaId) {
            const empresaHasActionsAllowed = verifyIntegrationMode('Setor', selectedEmpresaId);
            setModuleActionsAllowed(empresaHasActionsAllowed);
        }
    }, [selectedEmpresaId, verifyIntegrationMode]);

    useEffect(() => {
        if (openForm) return;
        setSelectedSector(null);

        const queryFilters = { ...Object.fromEntries(searchParams) }

        const tableFilters = { ...queryFilters };
        delete tableFilters.page;
        delete tableFilters.perPage;

        const activeFilters = Object.keys(tableFilters).length;

        setActiveFilters(activeFilters);
        setIsOpenFilters(activeFilters > 0);

    }, [openForm, searchParams]);

    useEffect(() => {
        const requiredPermissionsView = ["admin", "admin_setores", "list_setores"];
        checkPermissionsAndRedirect(requiredPermissionsView);
    }, []);

    return (<>
        <Helmet title="Setores" defer={false} />

        <Grid
            container
            direction="row"
            justifyContent="flex-start"
            alignItems="stretch"
            spacing={3}
        >
            <Grid item xs={12}>
                <Breadcrumbs>
                    <LinkNavegacao to="/">
                        <HomeIcon fontSize="small" />
                    </LinkNavegacao>
                    <Typography variant='span'>Gerenciamento</Typography>
                    <LinkNavegacao to="/gerenciamento/organizacao">Organização</LinkNavegacao>
                    <Typography variant='span'>Setores</Typography>
                </Breadcrumbs>

                <PageTitle
                    icon={<PuzzleIcon fontSize='large' />}
                    title='Setores'
                    description='Administre os setores de sua(s) empresa(s).'
                />
            </Grid>

            <Grid item xs={12}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent={'space-between'}>
                    <FiltersButton
                        isOpenFilters={isOpenFilters}
                        setIsOpenFilters={setIsOpenFilters}
                        activeFilters={activeFilters}
                    />

                    {hasPermission(["admin", "admin_setores", "create_setores"]) &&
                        <Typography sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                onClick={() => setOpenForm(true)}
                            >
                                Cadastrar novo setor
                            </Button>
                        </Typography>
                    }
                </Stack>
            </Grid>

            <ItemsTableFilter
                isOpenFilters={isOpenFilters}
                isLoadingList={isLoading}
                empresas={empresasData?.data?.data || []}
                item={"Setor"}
            />

            <Grid item xs={12}>
                <TableContainer component={Paper}>
                    <CustomToolbarExportTable
                        rows={setores}
                        columns={columns}
                        reportName="relatorio_setores"
                        reportPdfTitle="RELATÓRIO DE SETORES"
                        getDataWithoutPaginate={() => listaSetores({ id_empresas: [selectedEmpresaId] })}
                        getSelectedEmpresa={() => empresas.find(empresa => empresa.id === Number(selectedEmpresaId))}
                    />

                    <Table aria-label="Setores">
                        <TableHead>
                            <TableRow>
                                <TableCell>Setor</TableCell>
                                <TableCell>Cód. Externo</TableCell>
                                <TableCell>Descrição</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? <LoadingTableRow />
                                : setores && setores?.length > 0
                                    ? setores.map((item) =>
                                        <SectorRow
                                            key={item.id}
                                            data={item}
                                            handleExibirAlerta={exibirAlerta}
                                            handleOpenEditForm={handleEditSector}
                                            handleUpdateStatus={updateSectorStatus}
                                            updateData={refetch}
                                            actionsAllowed={moduleActionsAllowed}
                                        />
                                    )
                                    : <EmptyTableRow />
                            }
                        </TableBody>
                    </Table>

                    <CustomTablePagination
                        numTotalItems={numTotalItems}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        setPage={setPage}
                        setRowsPerPage={setRowsPerPage}
                    />
                </TableContainer>
            </Grid>
        </Grid>

        <SectorForm
            open={openForm}
            setOpen={setOpenForm}
            selectedSector={selectedSector}
            empresas={getEmpresasFilteredByModule('Setor', empresas, !selectedSector)}
            actionsAllowed={moduleActionsAllowed}
            updateData={refetch}
        />
    </>);
}

export default Sectors;